import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import {
  Sparkles,
  Shield,
  ShieldCheck,
  Scale,
  Zap,
  ChevronRight,
  ArrowLeft,
  TrendingUp,
  CheckCircle2,
  MessageSquareQuote,
  Download,
  HelpCircle,
  BookOpen,
  Target,
  Flame,
  X,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Snackbar, Alert, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import { useAuth } from "AuthContext";
import RadarChart6 from "./components/RadarChart6";
import { getAbilityBasis } from "./components/AbilityBasisInfo";
import StrategyDetailModal from "./components/StrategyDetailModal";
import FullKpiPanel, { labelForWarning } from "./components/FullKpiPanel";
import QuotaCard from "./components/QuotaCard";
import { TYPO, FONT, CHART } from "./typography";
// 註：DotBar 已被 linear progress bar 取代（窄欄位下 dot 會跑出邊緣），留檔備用
import Sparkline from "./components/Sparkline";
import { SYMBOL_SHORT_MAP } from "utils/symbols";
import {
  getSchool, getMove, getTemplate, getStrategyType,
  AMBITION_TO_RISK_TIER, eliteToRadar, RADAR_AXES,
} from "./utils/genieMapping";
import {
  wishStrategy, generateWishToken,
  pickStrategy, fetchMyStrategies,
} from "./utils/genieApi";
import { resetGenieAll } from "./utils/genieSelection";
import { LanguageContext } from "context/LanguageContext";
import { COLORS } from "theme/tokens";

// 簡易插值：fillTemplate('剩餘 {n} 次', { n: 3 }) → '剩餘 3 次'
const fillTemplate = (str, vars = {}) =>
  String(str ?? '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : ''));

// 時間框架中譯（ReportModal 顯示用）— 後端 timeframe = M1/M5/M15/M30/H1/H4...
const TIMEFRAME_LABEL = {
  M1: '1 分鐘 K 線',
  M5: '5 分鐘 K 線',
  M15: '15 分鐘 K 線',
  M30: '30 分鐘 K 線',
  H1: '1 小時 K 線',
  H4: '4 小時 K 線',
  D1: '日 K 線',
};
const tfLabel = (tf) => TIMEFRAME_LABEL[tf] || (tf ? `${tf} K 線` : '—');

const MASGenie = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // ?force=true 帶來時跳過「有訂閱自動 redirect」邏輯，強制留在許願頁
  // (從 sidebar「策略許願」+「我的精靈策略」頁的「再次許願」按鈕進來都會帶這個)
  const forceWishMode = useMemo(
    () => new URLSearchParams(location.search).get("force") === "true",
    [location.search]
  );

  // ── i18n：載入精靈主頁語言包（四語 zh-TW/zh-CN/en-US/en-GB）
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("MASGenie", "mas-genie/MASGenie/MASGenie_lang");
  }, [language]);
  const L = useMemo(() => componentLanguageData.MASGenie?.Main || {}, [componentLanguageData]);
  // 在地化時間框架標籤（缺值回退到模組內 tfLabel 中文）
  const tfLabelL = (tf) => {
    if (!tf) return '—';
    return (L.tf && L.tf[tf]) || (L.tf?.fallback && fillTemplate(L.tf.fallback, { tf })) || tfLabel(tf);
  };
  // 在地化雷達 6 軸標籤（傳給 RadarChart6）
  const radarAxes = useMemo(
    () => RADAR_AXES.map((a) => ({ key: a.key, label: L.radar?.[a.key] || a.label })),
    [L]
  );
  // per-axis 能力依據（傳給雷達：每軸旁一顆 ⓘ → 點跳該軸自己的 tooltip）
  const _ab = getAbilityBasis(language);
  const abAxisInfo = Object.fromEntries((_ab.items || []).map((it) => [it.key, { param: it.param, desc: it.desc }]));
  const abBasedOn = _ab.basedOn;
  // strategyType → 在地化名稱（key 查語言包，缺值回退中文 name）
  const stypeName = (st) => (st && L.stype?.[`${st.key}_name`]) || st?.name || '';
  // AI 名稱（依 UI 語言挑欄位；缺值回 null）— 菁英 AI 命名，優先於寫死的策略類型名
  const eliteAiName = (elite) => {
    const e = elite || {};
    if (language === 'zh-CN') return e.nameZhCn || null;
    if ((language || '').startsWith('en')) return e.nameEn || null;
    return e.nameZhTw || null;
  };

  // 401 → 統一登出 + 導向登入
  // 401 攔截改在 App.js 全域註冊一次（避免兩頁互相覆蓋）
  const [step, setStep] = useState('intro');
  const [selectedAmbition, setSelectedAmbition] = useState(null);
  const [meditationText, setMeditationText] = useState(L.meditating?.analyzing || '正在分析 G10 貨幣對趨勢...');
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [showOtherMoves, setShowOtherMoves] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'info' });
  const [subscribing, setSubscribing] = useState(false);
  const ambitionFooterRef = useRef(null);
  // 防 async fetch 在 component unmount 後 setState（React warning）
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  // Revelation 卡點「查看計畫詳情」→ 跳 modal（取代原本 setStep('report') 跳新頁）
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportKpiTab, setReportKpiTab] = useState('summary'); // 查看方案 modal 分頁：'summary' 重點摘要 | 'advanced' 進階指標

  const showToast = (msg, severity = 'info') => setToast({ open: true, msg, severity });

  // 訂閱策略 — POST /api/mas-genie/my-strategies
  const handleSubscribe = async () => {
    if (!selectedStrategy?.eliteId || !token) {
      showToast(L.toast?.subErrNoId || '狀態異常：找不到策略 ID，請重新挑選', 'error');
      return;
    }
    setSubscribing(true);
    try {
      const elite = selectedStrategy.elite || {};
      // 動態建議手數 — 高 MDD 策略給較小的預設值（依 doc Ch 12 範例）
      // MDD > 20% → 0.05，否則 0.1
      const suggestedVolume = (Number(elite.maxDrawdown) > 20) ? 0.05 : 0.1;
      const result = await pickStrategy(token, {
        eliteId: selectedStrategy.eliteId,
        nickname: null,  // 不自動填暱稱（沒改名就 null）→ 顯示一律走「AI 名 → symbol+類型」fallback，避免把舊類型名存進 nickname
        // 帶預設值給 EXE 端 — 不傳會收到 capital=0/lots=0 無法運行
        // 用戶之後在 EXE 端可改；網頁不暴露這些欄位（PO 決定）
        volumeOverride: suggestedVolume,
        capitalAllocation: 10000,
      });
      showToast(fillTemplate(L.toast?.subOk || '已成功訂閱「{name}」', { name: result.nickname || selectedStrategy.title }), 'success');
      // 訂閱成功 → refresh quota，下次進精靈會看到新的 used count
      refreshQuota();
      // 短延遲後導向我的精靈策略頁；帶 justSubscribed 旗標 → 首次儲存者會看到「如何開始交易」導引
      setTimeout(() => navigate('/my-genie-strategies', { state: { justSubscribed: true } }), 1200);
    } catch (err) {
      if (err.code === 'plan-insufficient' || err.status === 403) {
        showToast(L.toast?.planNo || '您的方案不支援訂閱精靈策略，請升級至 TRIAL 以上', 'warning');
        setTimeout(() => navigate('/plans'), 1500);
      } else if (err.code === 'duplicate-subscription') {
        // 409：用戶已訂閱過同一個 elite（避免重複）
        showToast(L.toast?.dupSub || '您已訂閱過此策略，前往「我的精靈策略」檢視', 'warning');
        setTimeout(() => navigate('/my-genie-strategies'), 1500);
      } else if (err.code === 'save-limit-reached' || err.status === 409) {
        const { currentCount, saveLimit } = err.body || {};
        const detail = currentCount != null && saveLimit != null ? `（${currentCount}/${saveLimit}）` : '';
        showToast(fillTemplate(L.toast?.saveLimit || '已達您方案的訂閱上限{detail}，請先到「我的精靈策略」移除舊策略', { detail }), 'warning');
        setTimeout(() => navigate('/my-genie-strategies'), 1500);
      } else if (err.status === 400 && /not ACTIVE|not LIVE-ready/i.test(err.message || '')) {
        showToast(L.toast?.notActive || '此策略目前無法訂閱（已下架或執行檔未就緒），請選其他策略', 'warning');
      } else if (err.status === 404) {
        showToast(L.toast?.notFound || '找不到此策略，可能已下架，請重新挑選', 'error');
      } else {
        showToast(fillTemplate(L.toast?.subFail || '訂閱失敗：{msg}', { msg: err.message || '請稍後再試' }), 'error');
      }
    } finally {
      setSubscribing(false);
    }
  };

  // API 結果
  const [revelationCards, setRevelationCards] = useState([]);
  const [revelationLoading, setRevelationLoading] = useState(false);
  const [revelationError, setRevelationError] = useState(null);
  const [poolSize, setPoolSize] = useState(0);
  // 冥想階段的「AI 思考路徑」進度（0~4，4 = 全部完成）
  const [traceIdx, setTraceIdx] = useState(0);

  // composedCards 用 useMemo cache — 只在 revelationCards 或 selectedAmbition 變動時重算
  // 雖然只有 3 個 item 計算便宜，但每次 render 重算會建立新 array reference → 引發底下 map 內元件重渲
  const composedCards = useMemo(() => {
    if (!revelationCards || revelationCards.length === 0) return [];
    const school = getSchool(selectedAmbition);
    // moveKey 順序固定 shou(保守) → heng(平衡) → rui(積極)，跟 banner 漸層由淺到深一致
    // 後端 cards[] 順序可能不固定，前端強制 sort 確保視覺穩定
    const MOVE_ORDER = { shou: 0, heng: 1, rui: 2 };
    return revelationCards
      .map(({ moveKey, elite }) => {
        const move = getMove(moveKey);
        // 2026-05-22 起改用新策略類型命名（趨勢突破/均線回歸/波段順勢/低頻防守/區間震盪）
        // 取代原本「派系+招式」(磐石守/天秤衡/雷霆銳…) 武俠風命名。
        // school/move 還保留供：tag 標籤 (進取/平衡/沉穩) + 風險顏色 + 派系徽章
        const strategyType = getStrategyType(selectedAmbition || 'balanced', moveKey);
        return {
          id: elite.id,
          elite,
          moveKey,
          school,
          move,
          strategyType,
          title: strategyType.name,
          tagline: strategyType.tagline,
          typeLabel: strategyType.type,
          tag: move.tag,
          radar: eliteToRadar(elite, moveKey),
        };
      })
      .sort((a, b) => (MOVE_ORDER[a.moveKey] ?? 99) - (MOVE_ORDER[b.moveKey] ?? 99));
  }, [revelationCards, selectedAmbition]);

  // 用戶配額（從 GET /my-strategies 的 quota 區塊拿）
  //   plan: { code, name, wizardSaveLimit, wizardMonthlyLimit, wizardExecLimit }
  //   quota: { used, limit (saveLimit), remaining, canSubscribeMore }
  //   monthly: 後端待補（目前 backend 還沒實作 monthlyUsed/Limit；前端先 ready）
  const [userQuota, setUserQuota] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [lastWishAmbition, setLastWishAmbition] = useState(null); // 後端記憶的上次 ambition
  const [wishMeta, setWishMeta] = useState({}); // { usedFallback, excludedRecent } — Revelation 顯示用
  const [quotaWarning, setQuotaWarning] = useState(null); // { type: 'save-full' | 'plan-locked' | 'monthly-full', ... }
  const [wishConfirmOpen, setWishConfirmOpen] = useState(false); // 許願 confirm dialog
  const [rateLimitUntil, setRateLimitUntil] = useState(0); // rate-limit 冷卻結束的 epoch ms（0 = 無限制）
  const [rateLimitRemaining, setRateLimitRemaining] = useState(0); // 派生倒數秒數（每秒由 interval 更新）
  // rate-limit 冷卻中每秒重算 remaining，到期歸零
  useEffect(() => {
    if (rateLimitUntil <= Date.now()) {
      setRateLimitRemaining(0);
      return;
    }
    const recompute = () => {
      const left = rateLimitUntil - Date.now();
      if (left <= 0) {
        setRateLimitRemaining(0);
        setRateLimitUntil(0);
        return false;
      }
      setRateLimitRemaining(Math.ceil(left / 1000));
      return true;
    };
    recompute(); // 立即觸發第一次（避免要等 1 秒才顯示）
    const t = setInterval(() => {
      if (!recompute()) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [rateLimitUntil]);

  // 離開 report 頁時把抽屜收回
  useEffect(() => {
    if (step !== 'report') setShowOtherMoves(false);
  }, [step]);

  // 🚦 智能導向用 ref — 確保進頁時的「有訂閱就跳到我的精靈策略」邏輯只跑一次
  //    refreshQuota 之後可能被許願流程觸發多次，沒這個 ref 會無窮跳轉
  const hasCheckedSubRedirectRef = useRef(false);

  // 進精靈時拉一次配額（用 my-strategies 回傳的 quota）
  const refreshQuota = useCallback(async () => {
    if (!token) return;
    try {
      const data = await fetchMyStrategies(token);
      if (!isMountedRef.current) return;
      setUserPlan(data?.plan || null);
      setUserQuota(data?.quota || null);
      // 記憶用戶上次許願的 ambition（後端記，用於 UI 預填）
      if (data?.lastWishAmbition) setLastWishAmbition(data.lastWishAmbition);

      // 🚦 智能導向（只在初次拉取時判斷一次）：
      //   - 用戶已有 ACTIVE 訂閱 → 跳去「我的精靈策略」看現有策略
      //   - 沒有訂閱 → 留在許願頁（intro step）
      //   - ?force=true (從 sidebar / 「再次許願」進來) → 跳過 redirect，留在許願頁
      //   - 用 replace: true 避免瀏覽器歷史留下 /mas-genie → /my-genie-strategies 的多餘跳轉
      if (!hasCheckedSubRedirectRef.current && !forceWishMode) {
        hasCheckedSubRedirectRef.current = true;
        const activeSubs = (data?.items || []).filter(s => s.status === 'ACTIVE');
        if (activeSubs.length > 0 && isMountedRef.current) {
          navigate('/my-genie-strategies', { replace: true });
        }
      }
    } catch (err) {
      console.error('refreshQuota failed', err);
      // 載入配額失敗不擋使用者，但會走到訂閱時才知道
    }
  }, [token, navigate, forceWishMode]);

  useEffect(() => { refreshQuota(); }, [refreshQuota]);

  // 進 ambition step 時，若用戶沒選且後端有記憶，預填上次的 ambition
  useEffect(() => {
    if (step === 'ambition' && !selectedAmbition && lastWishAmbition) {
      setSelectedAmbition(lastWishAmbition);
    }
  }, [step, selectedAmbition, lastWishAmbition]);

  // 點「開始許願」/ 任一 ambition 卡時的前置檢查
  // 規則：
  //   - plan.wizardSaveLimit === 0 → 硬擋（方案完全不支援）
  //   - quota.canSubscribeMore === false → 軟提示（已滿，建議刪舊）
  //   - monthlyUsed >= monthlyLimit → 硬擋（後端待補 monthly 計數機制）
  //   - 通過 → 進 ambition / meditating
  const preflightQuotaCheck = () => {
    if (!userPlan || !userQuota) return true; // 配額沒載入到也放行（fallback）

    const monthlyLimit = userPlan.wizardMonthlyLimit;
    const monthlyUsed = userQuota.monthlyUsed; // 後端待補
    const saveLimit = userPlan.wizardSaveLimit;

    // 1. 方案完全不支援
    if (saveLimit === 0) {
      setQuotaWarning({
        type: 'plan-locked',
        title: L.qw?.planLockedTitle || '您的方案不支援精靈策略',
        body: fillTemplate(L.qw?.planLockedBody || '當前方案：{plan}。請升級至 TRIAL（15 天免費試用）或以上才能訂閱精靈策略。', { plan: userPlan.name }),
        primaryAction: { label: L.qw?.gotoPlans || '前往方案頁', onClick: () => navigate('/plans') },
        secondaryAction: null,
      });
      return false;
    }

    // 2. 月配額用完（硬擋，後端目前還沒傳 monthlyUsed，這條暫時 no-op）
    if (monthlyLimit > 0 && monthlyUsed != null && monthlyUsed >= monthlyLimit) {
      setQuotaWarning({
        type: 'monthly-full',
        title: L.qw?.monthlyTitle || '策略精靈已用完',
        bodyMain: L.qw?.monthlyMain || '您的策略精靈本月使用次數已用完',
        bodyAlert: fillTemplate(L.qw?.monthlyAlert || '本月配額：{limit} 次，皆已使用', { limit: monthlyLimit }),
        bodyHint: L.qw?.monthlyHintDefault || '下個月 1 號重置，或升級更高方案，立即取得更多次數。',
        primaryAction: { label: L.qw?.upgrade || '升級方案', onClick: () => navigate('/plans') },
        secondaryAction: null,   // 移除「下次再來」死路按鈕，只留升級方案＋右上角 X
      });
      return false;
    }

    // 3. 儲存上限滿（軟提示，可繼續看但訂閱會被擋）
    //    導購優先：主按鈕「升級方案」→ /plans（提高上限、轉換付費）；次按鈕「前往我的策略刪除」
    //    移除原「我先看看其他選項」死路按鈕（看了也還是不能新增）；要關閉可點右上角 X
    if (userQuota.canSubscribeMore === false) {
      setQuotaWarning({
        type: 'save-full',
        title: L.qw?.saveFullTitle || '訂閱數已達上限',
        body: fillTemplate(L.qw?.saveFullBody || '您目前已有 {used}/{limit} 個精靈策略。\n升級方案可提高上限，或刪除一個舊的再訂閱。', { used: userQuota.used, limit: userQuota.limit }),
        primaryAction: { label: L.qw?.upgrade || '升級方案', onClick: () => navigate('/plans') },
        secondaryAction: { label: L.qw?.gotoMyDelete || '前往我的策略刪除', onClick: () => navigate('/my-genie-strategies') },
      });
      return false;
    }

    return true;
  };

  // 進入冥想階段 → POST /wish 扣月配額 + 取後端已挑好的 3 張卡
  // 同 ambition 重送會帶相同 wishToken（避免雙擊扣兩次配額），後端會回 idempotentReplay=true
  const wishTokenRef = useRef(null);
  const [idempotentReplay, setIdempotentReplay] = useState(false);
  useEffect(() => {
    if (step !== 'meditating' || !token || !selectedAmbition) return;
    const ambitionKey = selectedAmbition;
    if (!wishTokenRef.current) wishTokenRef.current = generateWishToken();

    setRevelationLoading(true);
    setRevelationError(null);
    setRevelationCards([]);
    setIdempotentReplay(false);

    wishStrategy(token, { ambition: ambitionKey, wishToken: wishTokenRef.current })
      .then((d) => {
        if (!isMountedRef.current) return;
        const cards = Array.isArray(d?.cards) ? d.cards : [];
        // 後端 cards[] 順序不固定（實測 rui,heng,shou），這裡強制 sort 成
        // shou(保守) → heng(平衡) → rui(積極)，跟卡片三色 banner 漸層由淺到深一致
        const MOVE_ORDER = { shou: 0, heng: 1, rui: 2 };
        const sortedCards = [...cards].sort(
          (a, b) => (MOVE_ORDER[a?.moveKey] ?? 99) - (MOVE_ORDER[b?.moveKey] ?? 99)
        );
        setRevelationCards(sortedCards);
        setPoolSize(d?.poolSize ?? cards.length);
        setIdempotentReplay(!!d?.idempotentReplay);
        // 旗標：池子小用了 fallback / 排除最近看過的
        setWishMeta({
          usedFallback: !!d?.usedFallback,
          excludedRecent: !!d?.excludedRecent,
        });
        // 順手同步月配額（成功扣完後）
        if (d?.monthlyUsed != null) {
          setUserQuota((q) => ({
            ...(q || {}),
            monthlyUsed: d.monthlyUsed,
            monthlyLimit: d.monthlyLimit,
            monthlyRemaining: d.monthlyRemaining,
            monthlyResetAt: d.monthlyResetAt,
          }));
        }
      })
      .catch((err) => {
        console.error('wishStrategy failed', err);
        if (!isMountedRef.current) return;
        handleWishError(err);
      })
      .finally(() => {
        if (isMountedRef.current) setRevelationLoading(false);
      });
  }, [step, selectedAmbition, token]);

  // 用 useCallback wrap，handleWishError 改 ambition 時不會無限重渲
  // 把 5 個錯誤碼集中處理 → 顯示對應 dialog 或 toast
  const handleWishError = (err) => {
    const code = err?.code;
    const body = err?.body || {};
    if (code === 'ambition-invalid') {
      showToast(L.qw?.ambitionInvalid || '請選擇心願類型再許願', 'error');
      setStep('ambition');
      return;
    }
    if (code === 'plan-insufficient') {
      setQuotaWarning({
        type: 'plan-locked',
        title: L.qw?.planLockedTitle || '您的方案不支援精靈策略',
        body: fillTemplate(L.qw?.planLockedBody2 || '當前方案：{plan}。請升級至 TRIAL（15 天免費試用）或以上。', { plan: body.currentPlan || userPlan?.name || '未知' }),
        primaryAction: { label: L.qw?.gotoPlans || '前往方案頁', onClick: () => navigate('/plans') },
        secondaryAction: { label: L.qw?.later2 || '稍後再說', onClick: () => { setQuotaWarning(null); setStep('intro'); } },
      });
      setStep('intro');
      return;
    }
    if (code === 'save-limit-reached') {
      setQuotaWarning({
        type: 'save-full',
        title: L.qw?.saveFullTitle || '訂閱數已達上限',
        body: fillTemplate(L.qw?.saveFullBody2 || '您目前已訂閱 {used}/{limit} 個策略。\n請先到「我的精靈策略」移除一個再來許願。', { used: body.saveUsed || '?', limit: body.saveLimit || '?' }),
        primaryAction: { label: L.qw?.gotoMy || '前往我的策略', onClick: () => navigate('/my-genie-strategies') },
        secondaryAction: { label: L.btn?.cancel || '取消', onClick: () => { setQuotaWarning(null); setStep('intro'); } },
      });
      setStep('intro');
      return;
    }
    if (code === 'monthly-limit-reached') {
      const resetAt = body.monthlyResetAt ? new Date(body.monthlyResetAt) : null;
      const daysLeft = resetAt ? Math.max(1, Math.ceil((resetAt - new Date()) / 86400000)) : null;
      setQuotaWarning({
        type: 'monthly-full',
        title: L.qw?.monthlyTitle || '策略精靈已用完',
        bodyMain: L.qw?.monthlyMain || '您的策略精靈本月使用次數已用完',
        bodyAlert: fillTemplate(L.qw?.monthlyAlert || '本月配額：{limit} 次，皆已使用', { limit: body.monthlyLimit || '?' }),
        bodyHint: daysLeft != null ? fillTemplate(L.qw?.monthlyHintDays || '下個月還有 {days} 天重置，或升級更高方案，立即取得更多次數。', { days: daysLeft }) : (L.qw?.monthlyHintDefault || '下個月 1 號重置，或升級更高方案，立即取得更多次數。'),
        primaryAction: { label: L.qw?.upgrade || '升級方案', onClick: () => navigate('/plans') },
        secondaryAction: null,   // 移除「下次再來」死路按鈕，只留升級方案＋右上角 X（step 已在下方設回 intro）
      });
      setStep('intro');
      return;
    }
    if (code === 'pool-empty') {
      setRevelationError(fillTemplate(L.qw?.poolEmpty || '目前 {tier} 帶沒可用策略，請換個目標或稍後再試', { tier: body.riskTier || '此風險' }));
      return;
    }
    if (code === 'rate-limit') {
      // 429：精靈對特定用戶的呼叫頻率上限（避免後端過載）
      const retryAfter = body.retryAfter ?? body.retry_after ?? 60;
      setRateLimitUntil(Date.now() + retryAfter * 1000);
      setQuotaWarning({
        type: 'rate-limited',
        title: L.qw?.rateTitle || '精靈忙線中',
        body: fillTemplate(L.qw?.rateBody || '為避免過載，許願有頻率限制。\n請等候 {sec} 秒後再試（按鈕會自動倒數解鎖）。', { sec: retryAfter }),
        primaryAction: { label: L.qw?.gotIt || '知道了', onClick: () => { setQuotaWarning(null); setStep('intro'); } },
        secondaryAction: null,
      });
      setStep('intro');
      return;
    }
    setRevelationError(err?.message || L.qw?.wishFail || '許願失敗，請稍後再試');
  };

  // 冥想動畫：輪播文字 + AI 思考路徑
  useEffect(() => {
    if (step === 'meditating') {
      const headlines = [
        L.meditating?.h1 || "步驟 1/6 · 連線策略引擎，初始化任務...",
        L.meditating?.h2 || "步驟 2/6 · 載入 6 年歷史市場資料中...",
        L.meditating?.h3 || "步驟 3/6 · 分析 G10 貨幣對趨勢與流動性...",
        L.meditating?.h4 || "步驟 4/6 · 跑 500 次回測迭代，計算 KPI...",
        L.meditating?.h5 || "步驟 5/6 · 通過風險與壓力驗證...",
        L.meditating?.h6 || "步驟 6/6 · 整合您的策略檔案，即將完成..."
      ];
      setMeditationText(headlines[0]);
      let h = 1;
      const headlineInterval = setInterval(() => {
        if (h < headlines.length) {
          setMeditationText(headlines[h]);
          h++;
        } else {
          clearInterval(headlineInterval);
        }
      }, 1100);

      setTraceIdx(0);
      const traceInterval = setInterval(() => {
        setTraceIdx((idx) => (idx < 4 ? idx + 1 : idx));
      }, 1500);

      // idempotentReplay = 後端說「這次是重放」→ 快速結束（1.2 秒），不再演完整 6.8 秒
      const duration = idempotentReplay ? 1200 : 6800;
      const timer = setTimeout(() => {
        setStep('revelation');
      }, duration);

      return () => {
        clearInterval(headlineInterval);
        clearInterval(traceInterval);
        clearTimeout(timer);
      };
    }
  }, [step, idempotentReplay]);

  // 「重新許願」/ 回到 intro → 清掉 wishToken，下次會產新的、扣新配額
  useEffect(() => {
    if (step === 'intro') {
      wishTokenRef.current = null;
    }
  }, [step]);

  const handleSelectAmbition = (id) => {
    // 改 ambition → 必須清掉 wishToken，否則下次 wish 會 idempotent replay 回上次 ambition 的卡
    if (id !== selectedAmbition) {
      wishTokenRef.current = null;
      // 順便清掉舊的 wish meta，避免上一個 ambition 的 chip 還顯示
      setWishMeta({});
      setIdempotentReplay(false);
      setRevelationError(null);
    }
    setSelectedAmbition(id);
    // 點選後自動 scroll 到底部按鈕，避免使用者不知道要往下找下一步
    setTimeout(() => {
      ambitionFooterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 120);
  };

  // 風險選項卡（截圖一/三/四 spec）：三狀態 Default / Hover / Selected
  //   Default: bg-white + border-slate-100 + subtle shadow
  //   Hover:   border 加深到 slate-300
  //   Selected: border-orange-500 + bg-orange-50（淡橘）+ orange shadow
  // icon：白底圓角小盒 + 純橘 lucide icon（不用派系色 bg）
  // 標題：橘色加粗；描述：深灰
  const GoalCard = ({ card }) => {
    const isSelected = selectedAmbition === card.id;
    return (
      <button
        key={card.id}
        onClick={() => handleSelectAmbition(card.id)}
        style={{
          display: 'flex', width: '100%', boxSizing: 'border-box',
          padding: 'clamp(12px, 2.2vh, 24px) clamp(16px, 2.5vw, 28px)',
          justifyContent: 'space-between', alignItems: 'center', gap: 20,
          borderRadius: 24,
          border: isSelected ? '1px solid #F36D26' : '1px solid transparent',
          background: isSelected ? COLORS.lighter : COLORS.grayLightest,
          boxShadow: '2px 2px 8px 0 rgba(60, 59, 59, 0.15)',
          textAlign: 'left', cursor: 'pointer',
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        {/* icon 外框 48×48 圓白底；內 lucide ~30 橘色 */}
        <div style={{ display: 'flex', width: 48, height: 48, justifyContent: 'center', alignItems: 'center', borderRadius: 999, background: COLORS.white, flexShrink: 0 }}>
          <card.icon size={30} strokeWidth={2.2} color={COLORS.main} />
        </div>

        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* 大標 — Display-M 24/500 橘 */}
            <h3 style={{ color: COLORS.main, ...TYPO.displayM, margin: 0 }}>{card.title}</h3>
            {/* 預填提示：後端記憶上次選的，幫用戶預先選中 */}
            {lastWishAmbition === card.id && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: COLORS.lighter, color: COLORS.main, border: '1px solid #F6C9A8' }}>
                {L.ambition?.lastPicked || '⭐ 您上次選的'}
              </span>
            )}
          </div>
          {/* 小標 — Body-M 14/400 黑 */}
          <p style={{ color: COLORS.black, ...TYPO.bodyM, margin: 0, marginTop: 4 }}>{card.desc}</p>
        </div>

        {/* 右側圓圈：selected → 橘色實心 + 8px 白點；未選 → 灰邊(#8A8A8A)空心 */}
        <div style={{
          width: 24, height: 24, flexShrink: 0, borderRadius: 12, boxSizing: 'border-box',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          border: isSelected ? '2px solid #F36D26' : '2px solid #8A8A8A',
          background: isSelected ? COLORS.main : 'transparent',
        }}>
          {isSelected && <span style={{ width: 8, height: 8, borderRadius: 999, background: COLORS.white }} />}
        </div>
      </button>
    );
  };

  // Revelation 卡片（截圖一 marketing style）：彩色 banner + 大 radar + 大字年化獲利 + 黑 CTA
  // 三張卡用「橘黃三階」配色：shou=保守橘 / heng=平衡黃 / rui=積極深橘
  // 三色 banner — PO 指定色票（2026-05-XX final）
  const REV_BANNER = {
    shou: { label: L.card?.bannerShou || '保守', color: COLORS.riskLow, glow: 'rgba(218, 141, 94, 0.20)' }, // 保守橘
    heng: { label: L.card?.bannerHeng || '平衡', color: COLORS.riskMid, glow: 'rgba(251, 192, 84, 0.20)' }, // 平衡黃
    rui:  { label: L.card?.bannerRui || '積極', color: COLORS.riskHigh, glow: 'rgba(252, 137, 21, 0.20)' }, // 積極深橘
  };
  // 配合三色 banner 的副標 marketing 文案
  const REV_TAGLINE = {
    shou: L.card?.taglineShou || '少一點出手，多一點把握',
    heng: L.card?.taglineHeng || '穩中求進，抓住更多機會',
    rui:  L.card?.taglineRui || '敢面對波動，追求更大的可能',
  };
  const RevelationCard = ({ card }) => {
    const moveKey = card.move.key;
    const banner = REV_BANNER[moveKey] || REV_BANNER.heng;
    const tagline = REV_TAGLINE[moveKey] || card.tagline;
    // 2026-09-14：期末考不到一年的策略不再顯示「預期年化獲利」。
    //   cagr =（1+期末考報酬）^(365/期末考天數)-1 —— LOW_FREQ 120 天是 3 次方、
    //   M5 21 天是 17 次方、M1 10 天是 36 次方；實測最誇張的一支 120 天 +30.8%
    //   被寫成年化 411%，抽卡頁就顯示「預期年化獲利 $41,140」。
    //   同時拿掉「annualReturn 缺值就用 monthlyReturn 自己複利」的後備路徑 ——
    //   那條路等於在前端再外推一次。
    const extrapolated = card.elite.annualIsExtrapolated === true;
    const apiAnnual = Number(card.elite.annualReturn);
    const annualizedPct = !extrapolated && Number.isFinite(apiAnnual) && apiAnnual > 0 ? apiAnnual : null;
    const yearProfit = annualizedPct != null ? Math.round(10000 * annualizedPct / 100) : null;
    const examDays = Number.isFinite(Number(card.elite.examDays)) ? Number(card.elite.examDays) : null;
    const examRet = Number.isFinite(Number(card.elite.totalReturn)) ? Number(card.elite.totalReturn) : null;
    // 平均多久一筆 —— 穩健池隨機某天開跑，30 天內有 1/3 機率連一次開倉都沒有。
    // 這個數字原本只在點進去的詳細視窗才看得到。
    // 節奏的數值換算在後端（flattenElite 的 tradePaceDays / tradePacePerDay），
    // 這裡只挑字串模板 —— 高頻策略若在前端各算各的，會出現「平均約 0 天一筆」。
    const paceText = card.elite.tradePacePerDay != null
      ? (L.card?.tradePaceDaily || '平均一天約 {n} 筆').replace('{n}', card.elite.tradePacePerDay)
      : (card.elite.tradePaceDays != null
          ? (L.card?.tradePace || '平均約 {n} 天一筆').replace('{n}', card.elite.tradePaceDays)
          : null);
    // 1b（2026-09-14）：有「近 12 個月回測」就優先顯示 —— 每支都是同一個 365 天窗，
    //   彼此可比，也不必把 120/21/10 天的期末考複利外推。
    //   ⚠️ 這裡顯示的值必須與後端 cardScorePct 同源，否則銳/衡/守 與 🏆 徽章會與
    //   畫面數字脫節（masGenieRoutes 的 MOVE_BY_RANK 註解記載過這個事故）。
    const r12 = card.elite.recent12m || null;
    const isR12 = card.elite.cardBasis === 'recent12m' && r12 && Number.isFinite(Number(r12.returnPct));
    const pct1 = (v) => `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`;
    const headLabel = isR12 ? (L.card?.recentResult || '近 12 個月回測')
      : extrapolated ? (L.card?.examResult || '期末考成績')
      : (L.card?.expectedAnnual || '預期年化獲利');
    const headValue = isR12 ? pct1(r12.returnPct)
      : extrapolated ? (examRet != null ? pct1(examRet) : '—')
      : (yearProfit != null ? `$${yearProfit.toLocaleString()}` : '—');
    const headSub = isR12
      ? (L.card?.recentWindow || '實測 {a} ~ {b}').replace('{a}', r12.from || '—').replace('{b}', r12.to || '—')
      : extrapolated
        ? (L.card?.examWindow || '實測 {n} 天的成績，未年化').replace('{n}', examDays != null ? examDays : '—')
        : (L.card?.afterCompound || '投入 $10,000，一年複利後');

    // 整張卡點擊 → 跳 ReportModal（取代原本 setStep('report') 跳新頁）
    const handleCardClick = () => {
      setSelectedStrategy({
        elite: card.elite,
        eliteId: card.elite.id,
        symbol: card.elite.symbol,
        timeframe: card.elite.timeframe,
        monthlyReturn: card.elite.monthlyReturn,
        annualReturn: card.elite.annualReturn,
        maxDrawdown: card.elite.maxDrawdown,
        profitFactor: card.elite.profitFactor,
        winRate: card.elite.winRate,
        tradeCount: card.elite.tradeCount,
        ambition: selectedAmbition,
        move: card.move.key,
        school: selectedAmbition,
        title: eliteAiName(card.elite) || stypeName(card.strategyType) || card.title,  // 對齊卡片：AI 名優先（訂閱 toast / modal fallback 都吃這個）
        strategyTypeKey: card.strategyType?.key,
        tagline: card.tagline,
        radar: card.radar,
        riskTier: card.elite.riskTier,
        riskLabel: card.elite.riskLabel,
        // 給 modal 用的附加：banner 色/label（直接從 outer scope 拿）
        bannerColor: banner.color,
        bannerLabel: banner.label,
      });
      setReportModalOpen(true);
    };

    // hover state — 用 React 控制（tailwind group-hover 無法配 banner.color dynamic）
    const [hovered, setHovered] = useState(false);

    return (
      // 整張卡可點擊；hover 時邊框/shadow/CTA 都換成 banner 色發光
      <button
        type="button"
        onClick={handleCardClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="text-left bg-white flex flex-col cursor-pointer focus:outline-none"
        style={{
          padding: 24, gap: 16, borderRadius: 20, boxSizing: 'border-box',
          border: `1px solid ${hovered ? banner.color : COLORS.grayLightest}`,
          background: COLORS.white,
          boxShadow: hovered
            ? `0 24px 50px ${banner.color}55, 0 0 0 4px ${banner.color}22`
            : '0 2px 6px 0 rgba(0, 0, 0, 0.20)',
          // hover 整張卡放大 1.06 + 上浮 8px，比其他兩張明顯凸出
          transform: hovered ? 'translateY(-8px) scale(1.06)' : 'translateY(0) scale(1)',
          transformOrigin: 'center center',
          transition: 'transform 0.25s ease-out, box-shadow 0.25s ease-out, border-color 0.15s ease-out',
          // hover 時拉到上層 z 避免被相鄰卡覆蓋
          zIndex: hovered ? 10 : 1,
          position: 'relative',
        }}
      >
        {/* 頂部 banner — align-self stretch，radius 2，16/500 白字；右上角漸層光暈 */}
        <div style={{ position: 'relative', alignSelf: 'stretch' }}>
          <div style={{ position: 'absolute', top: 0, right: -0.333, width: 120, height: 120, borderRadius: 12, opacity: 0.8, background: banner.glow, filter: 'blur(20px)', pointerEvents: 'none' }} />
          <div
            style={{ position: 'relative', display: 'flex', height: 30, padding: '5px 0 6px 0', justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch', borderRadius: 2, background: banner.color, color: COLORS.white, textAlign: 'center', ...TYPO.title, lineHeight: 'normal' }}
          >
            {banner.label}
          </div>
        </div>

        {/* 標題 + 副標 — 大標 Display-M 24/500 #191C1D；小標 Body-M 14/400 灰 */}
        <div style={{ alignSelf: 'stretch', textAlign: 'center' }}>
          <h3 style={{ color: '#191C1D', ...TYPO.displayM, margin: 0 }}>{eliteAiName(card.elite) || stypeName(card.strategyType) || card.title}</h3>
          <p style={{ color: COLORS.grayDark, ...TYPO.bodyM, margin: 0 }}>{tagline}</p>
        </div>

        {/* 大 radar — 沿用我的策略卡片雷達樣式（outerRadius 90% / 軸標籤 10 / 頂點 9） */}
        <div style={{ alignSelf: 'stretch', width: '100%', aspectRatio: '360 / 280' }}>
          <RadarChart6 values={card.radar} color={banner.color} variant="full" height="100%" instanceKey={`rev-${card.id}`} labelFontSize={CHART.radarLabelSize} dotRadius={CHART.radarDotRadius} dotFontSize={CHART.radarValueSize} outerRadius="90%" axes={radarAxes} axisInfo={abAxisInfo} basedOn={abBasedOn} />
        </div>

        {/* 預期年化獲利 box — radius8 / 灰底 #F6F6F6 / padding16 / gap4 */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, alignSelf: 'stretch', padding: 16, borderRadius: 8, background: COLORS.grayLightest }}>
          <p style={{ alignSelf: 'stretch', color: COLORS.black, textAlign: 'center', ...TYPO.bodyM, margin: 0 }}>{headLabel}</p>
          <p style={{ alignSelf: 'stretch', color: COLORS.main, textAlign: 'center', ...TYPO.numDisplayL, margin: 0 }}>{headValue}</p>
          <p style={{ alignSelf: 'stretch', color: COLORS.grayDark, textAlign: 'center', ...TYPO.bodyM, margin: 0 }}>{headSub}</p>
          {paceText && (
            <p style={{ alignSelf: 'stretch', color: COLORS.grayDark, textAlign: 'center', ...TYPO.bodyM, margin: 0 }}>
              {paceText}
            </p>
          )}
        </div>

        {/* CTA — 黑底白字 h48 radius8；hover 時從黑變 banner 色 */}
        <div
          style={{ display: 'flex', height: 48, padding: '12px 32px', boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', gap: 10, alignSelf: 'stretch', marginTop: 'auto', borderRadius: 8, background: hovered ? banner.color : COLORS.black, color: COLORS.white, textAlign: 'center', ...TYPO.title, transition: 'background 0.2s' }}
        >
          {L.card?.viewPlan || '查看計劃詳情'}
        </div>
      </button>
    );
  };

  // 頂部配額兩張卡（策略儲存／策略精靈）— 共用 QuotaCard，許願流程各步驟一致
  const renderQuotaCards = () => (
    <div className="grid grid-cols-2 gap-4 md:gap-5 mb-4 mt-1 w-full">
      <QuotaCard
        icon="/images/icon/genie/genie.png"
        label={L.quota?.genie || '策略精靈'}
        remainingLabel={L.quota?.remaining || '剩餘'}
        remaining={userQuota?.monthlyRemaining ?? '—'}
        limit={userQuota?.monthlyLimit}
        tooltip={userQuota?.monthlyLimit === -1 ? (L.quota?.genieUnlimited || '您的方案本月精靈使用次數無上限') : fillTemplate(L.quota?.genieRemain || '本月還可使用 {n} 次策略精靈', { n: userQuota?.monthlyRemaining ?? 0 })}
      />
      <QuotaCard
        icon="/images/icon/genie/download.png"
        label={L.quota?.save || '策略儲存'}
        remainingLabel={L.quota?.remaining || '剩餘'}
        remaining={userQuota?.remaining ?? '—'}
        limit={userQuota?.limit}
        tooltip={userQuota?.limit === -1 ? (L.quota?.saveUnlimited || '您的方案可保存策略無上限') : fillTemplate(L.quota?.saveRemain || '還可保存 {n} 個策略', { n: userQuota?.remaining ?? 0 })}
      />
    </div>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox position="relative" sx={{ minHeight: "85vh", overflow: "hidden", pt: '12px', display: 'flex', flexDirection: 'column' }}>
        {/* 配額卡釘最上面（不跟著置中）；下面步驟內容在剩餘空間垂直置中 */}
        <div className="w-full px-6" style={{ paddingTop: 10, paddingBottom: 20, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* 頂部配額 cards — 固定在最上方，四個步驟共用（截圖二 spec）
              兩張並排，給用戶第一眼看到自己有多少額度；refreshQuota 拉 GET /my-strategies 的 plan + quota */}
          {(userQuota && userPlan) && renderQuotaCards()}

          {/* 步驟內容置中容器：高螢幕上下留白平均（看起來是刻意置中），
              矮螢幕內容比框高時自然從頂端開始排、不裁切（min-height:auto 會撐高，不用 overflow） */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* 螢幕一：起始登錄頁 Intro Page */}
          {step === 'intro' && (
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000 w-full flex-1 flex flex-col">

              {/* ───────────────────────── Hero block — gif logo + 文案 + stepper + CTA
                  上下 padding 用 clamp(vh)：矮螢幕（含筆電顯示縮放）自動變緊湊塞進一頁
                  2026-06-10：卡片 flex-1 撐滿可用高度（大螢幕不再短一截），內容垂直置中 */}
              <div className="bg-white rounded-[32px] px-6 md:px-8 border border-slate-100 shadow-md flex-1 flex flex-col" style={{ paddingTop: 'clamp(16px, 3vh, 32px)', paddingBottom: 'clamp(16px, 3vh, 32px)' }}>
                {/* 已有精靈策略時，左上角顯示返回我的策略 */}
                {userQuota?.used > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate('/my-genie-strategies')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      color: '#64748b', ...TYPO.bodyM, fontWeight: 500,
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: 0, marginBottom: 'clamp(6px, 1.2vh, 16px)',
                      textDecorationLine: 'underline', textUnderlineOffset: 'auto',
                    }}
                  >
                    <ArrowLeft size={16} /> {L.btn?.backToMyStrategies || '返回我的策略'}
                  </button>
                )}
                <div className="flex flex-col items-center text-center flex-1 justify-center">

                  {/* 中央動態 logo — gif 本身就有橘色背景，不再加 blur pulse 光暈 */}
                  <img
                    src="/images/logo/genie_1.gif"
                    alt={L.brand?.name || 'MAS 策略精靈'}
                    style={{ width: 'clamp(60px, 10vh, 96px)', height: 'clamp(60px, 10vh, 96px)', aspectRatio: '129 / 128', objectFit: 'cover', marginBottom: 'clamp(4px, 1.2vh, 12px)' }}
                  />

                  {/* 標題 — Display-L 32/500/48；MAS 策略精靈 橘字 */}
                  <h1 style={{ color: COLORS.black, textAlign: 'center', ...TYPO.displayL, margin: 0 }}>
                    {L.intro?.hi || '嗨！我是您的'} <span style={{ color: COLORS.main }}>{L.brand?.name || 'MAS 策略精靈'}</span>{L.intro?.bang || '！'}<br />
                    {L.intro?.line2 || '我會一步步幫您找到適合的交易策略'}
                  </h1>
                  {/* 副標 — Title 16/500/24 灰 #8A8A8A */}
                  <p style={{ color: COLORS.grayDark, textAlign: 'center', ...TYPO.title, marginTop: 'clamp(4px, 1vh, 10px)', marginBottom: 0 }}>
                    {L.intro?.subtitle || '不用先懂交易，回答幾個問題，我會幫您找到適合的策略。'}
                  </p>

                  {/* 4 步驟流程 — 純文字 stepper（Body-M 14/400 橘字；不像按鈕，避免誤點）*/}
                  <div className="flex items-center justify-center flex-wrap" style={{ padding: 'clamp(6px, 1.8vh, 18px) 0', gap: 10 }}>
                    {[
                      { n: 1, label: L.intro?.step1 || '說出您的目標' },
                      { n: 2, label: L.intro?.step2 || '精靈為您分析' },
                      { n: 3, label: L.intro?.step3 || '篩選策略' },
                      { n: 4, label: L.intro?.step4 || '一鍵啟用' },
                    ].map((s, i, arr) => (
                      <React.Fragment key={s.n}>
                        <span className="inline-flex items-center" style={{ gap: 6 }}>
                          <span
                            className="flex items-center justify-center shrink-0"
                            style={{ width: 24, height: 24, borderRadius: 12, background: COLORS.lighter, color: COLORS.main, textAlign: 'center', ...TYPO.bodyM, fontFamily: FONT.OUTFIT }}
                          >
                            {s.n}
                          </span>
                          <span style={{ color: COLORS.main, textAlign: 'center', ...TYPO.bodyM }}>{s.label}</span>
                        </span>
                        {i < arr.length - 1 && <ChevronRight size={16} className="text-slate-400" />}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* CTA — 橘色 gradient 按鈕（截圖二 spec：433×60 / radius16 / Title 16-500 白字）*/}
                  <button
                    onClick={() => { if (preflightQuotaCheck()) setStep('ambition'); }}
                    disabled={rateLimitRemaining > 0}
                    className="group transition-transform outline-none focus:outline-none focus-visible:outline-none hover:scale-[1.02] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                    style={{
                      display: 'flex', width: '100%', maxWidth: 433, height: 60,
                      padding: '20px 0 21px 0', boxSizing: 'border-box',
                      justifyContent: 'center', alignItems: 'center', gap: 8,
                      borderRadius: 16, border: 'none',
                      background: rateLimitRemaining > 0 ? '#94a3b8' : 'linear-gradient(90deg, #F5C965 0%, #F36D26 100%)',
                      boxShadow: rateLimitRemaining > 0 ? 'none' : '4px 4px 9px 0 rgba(222, 86, 15, 0.20)',
                      color: COLORS.white, textAlign: 'center', ...TYPO.title, lineHeight: 'normal',
                      cursor: rateLimitRemaining > 0 ? 'not-allowed' : 'pointer',
                    }}
                    title={rateLimitRemaining > 0 ? (L.intro?.genieResting || '精靈休息中，請稍候') : undefined}
                  >
                    {rateLimitRemaining > 0
                      ? <>{fillTemplate(L.intro?.genieBusy || '精靈忙線中（{n} 秒）', { n: rateLimitRemaining })}</>
                      : (
                        <>
                          {L.intro?.start || '開始體驗'}
                          <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                  </button>

                  {/* 第二行 CTA — 橘色底線文字 link（截圖二 spec：Title 16/500 + underline）*/}
                  <Link
                    to="/strategy-summary"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 2, marginTop: 'clamp(6px, 1.4vh, 14px)',
                      color: COLORS.main, fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
                      ...TYPO.title,
                      textDecorationLine: 'underline', textDecorationStyle: 'solid',
                      textUnderlineOffset: 'auto', textUnderlinePosition: 'from-font',
                    }}
                  >
                    {L.intro?.gotoAssistant || '→ 我有策略想法，前往 AI 策略助理'}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* 螢幕二：許願階段 Step 1: Ambition Wish */}
          {step === 'ambition' && (
            <div className="w-full animate-in fade-in slide-in-from-right-8 duration-500">

              {/* Hero block 包住整個 ambition 區（截圖二 spec）；上下 padding 用 clamp(vh) 隨高度縮 */}
              <div className="bg-white rounded-[32px] px-6 md:px-8 border border-slate-100 shadow-md relative" style={{ paddingTop: 'clamp(16px, 3vh, 32px)', paddingBottom: 'clamp(16px, 3vh, 32px)' }}>
                {/* 右上角「重新選擇」按鈕 — 純橘外框 + 透明底 + 橘字（無 hover 背景）*/}
                <button
                  onClick={() => setSelectedAmbition(null)}
                  className="absolute top-5 right-5 md:top-6 md:right-6 px-3 py-1.5 rounded-xl text-xs md:text-sm font-bold transition-colors"
                  style={{
                    border: '1.5px solid #f97316',
                    color: COLORS.main,
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#ea580c'; e.currentTarget.style.borderColor = '#ea580c'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = COLORS.main; e.currentTarget.style.borderColor = COLORS.main; }}
                >
                  {L.btn?.reselect || '重新選擇'}
                </button>
                {/* 標題置中；卡片 + 按鈕限 max-w-2xl 不滿版（PO spec） */}
                <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2 text-center tracking-tight pt-8 md:pt-4">{L.ambition?.title || '您希望的投資風格？'}</h2>
                <p className="text-sm md:text-base text-slate-500 text-center leading-relaxed max-w-2xl mx-auto" style={{ marginBottom: 'clamp(8px, 2vh, 20px)' }}>
                  {L.ambition?.subtitle || '跟精靈許願，選擇不同的策略去匹配不同的交易商品、交易頻率、獲利報酬'}
                </p>
                <div className="mx-auto" style={{ maxWidth: 560 }}>
                  <div className="flex flex-col" style={{ gap: 'clamp(8px, 1.6vh, 12px)', marginBottom: 'clamp(12px, 2.4vh, 24px)' }}>
                    {[
                      { id: 'steady',   title: L.ambition?.steadyTitle || '保守觀察', desc: L.ambition?.steadyDesc || '預期小額獲利，低風險。',              icon: Shield },
                      { id: 'balanced', title: L.ambition?.balancedTitle || '均衡增長', desc: L.ambition?.balancedDesc || '追求平衡與持續成長，適應多變市場。', icon: Scale },
                      { id: 'hunter',   title: L.ambition?.hunterTitle || '積極增長', desc: L.ambition?.hunterDesc || '可接受較大波動，追求較高的回報。',   icon: TrendingUp },
                    ].map((card) => <GoalCard key={card.id} card={card} />)}
                  </div>

                  {/* 下一步按鈕 — 跟卡片同寬置中 */}
                  <div ref={ambitionFooterRef}>
                  <button
                    disabled={!selectedAmbition || !userQuota || !userPlan || rateLimitRemaining > 0}
                    onClick={() => {
                      if (!preflightQuotaCheck()) return;
                      setWishConfirmOpen(true);
                    }}
                    className={`w-full rounded-[24px] font-black text-xl md:text-2xl text-white transition-all flex items-center justify-center gap-2 border-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 ${
                      selectedAmbition && userQuota && userPlan && rateLimitRemaining === 0
                        ? 'hover:scale-[1.01] active:scale-95'
                        : 'cursor-not-allowed opacity-60'
                    }`}
                    style={{
                      paddingTop: 'clamp(10px, 1.8vh, 16px)', paddingBottom: 'clamp(10px, 1.8vh, 16px)',
                      ...(selectedAmbition && userQuota && userPlan && rateLimitRemaining === 0
                        ? {
                            background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #ea580c 100%)',
                            boxShadow: '0 12px 32px rgba(249, 115, 22, 0.35)',
                            border: 'none',
                          }
                        : { background: '#cbd5e1', border: 'none' }),
                    }}
                    title={rateLimitRemaining > 0 ? (L.intro?.genieResting || '精靈休息中，請稍候') : (!userQuota ? (L.ambition?.loadingQuota || '正在載入配額資訊...') : undefined)}
                  >
                    {rateLimitRemaining > 0
                      ? <>{fillTemplate(L.intro?.genieBusy || '精靈忙線中（{n} 秒）', { n: rateLimitRemaining })}</>
                      : (!userQuota && selectedAmbition ? (L.btn?.loading || '載入中...') : (L.btn?.next || '下一步'))}
                    {rateLimitRemaining === 0 && <ChevronRight size={24} />}
                  </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 螢幕三：精靈冥想 — 翻書讀取資料 → 粒子流 → 策略水晶成形
              三段式場景：左側精靈靠近 → 中央魔法書翻頁讀取資料 → 右側水晶成形 */}
          {step === 'meditating' && (
            <>
              {/* 冥想動畫內容（配額卡已抽到上層固定） */}
              <div className="max-w-3xl mx-auto text-center mt-4 flex flex-col items-center">
              <div className="relative w-full max-w-[640px] h-[230px] mb-6 flex items-center justify-around px-4">

                {/* 左：精靈 */}
                <div className="relative flex flex-col items-center">
                  <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 via-orange-400 to-orange-500 flex items-center justify-center shadow-2xl shadow-orange-200 animate-bounce-slow">
                    <div className="absolute inset-0 rounded-full bg-white/20 animate-pulse"></div>
                    <Sparkles className="text-white relative z-10" size={36} />
                  </div>
                  <div className="text-xs font-bold text-slate-600 mt-3">{L.meditating?.genie || '精靈'}</div>
                  {/* 精靈周圍小光 */}
                  <Sparkles className="absolute -top-2 -right-2 text-amber-400 animate-float" size={14} />
                  <Sparkles className="absolute top-10 -left-3 animate-float" size={12} style={{ color: COLORS.main, animationDelay: '0.6s' }} />
                </div>

                {/* 粒子流 1 — 精靈 → 書本 */}
                <div className="absolute top-1/2 left-[18%] right-[60%] h-2 -translate-y-1/2 pointer-events-none">
                  {[0, 0.4, 0.8].map((d, i) => (
                    <span
                      key={i}
                      className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full shadow-md shadow-orange-200 animate-particle-flow-1"
                      style={{ animationDelay: `${d}s`, background: '#F5A24B' }}
                    />
                  ))}
                </div>

                {/* 中：魔法書 */}
                <div className="relative animate-book-float">
                  {/* 書本背景光暈 */}
                  <div className="absolute -inset-6 rounded-full blur-3xl opacity-30 animate-pulse" style={{ background: COLORS.main }}></div>
                  {/* 書本主體 */}
                  <div className="relative w-32 h-28 bg-gradient-to-br from-white to-orange-50 rounded-xl shadow-2xl border-2 border-orange-200 flex items-center justify-center overflow-hidden">
                    <BookOpen className="relative z-0" size={64} style={{ color: COLORS.main }} />
                    {/* 翻頁效果 — 一張白紙從右翻起 */}
                    <div
                      className="absolute top-1.5 left-1/2 w-[58px] h-[100px] bg-white shadow-lg origin-left animate-page-flip rounded-r"
                      style={{ borderRight: '1px solid #FBD3B8' }}
                    ></div>
                    <div
                      className="absolute top-1.5 left-1/2 w-[58px] h-[100px] bg-white shadow-md origin-left animate-page-flip rounded-r"
                      style={{ borderRight: '1px solid #FEF1EA', animationDelay: '0.7s' }}
                    ></div>
                  </div>
                  {/* 書頁飛出星光 */}
                  <Sparkles className="absolute -top-3 left-1/4 animate-stream-up" size={14} style={{ color: COLORS.riskHigh }} />
                  <Sparkles className="absolute -top-1 right-1/4 animate-stream-up" size={12} style={{ color: COLORS.main, animationDelay: '0.4s' }} />
                  <Sparkles className="absolute -top-5 left-1/2 text-amber-500 animate-stream-up" size={16} style={{ animationDelay: '0.8s' }} />
                  <div className="text-xs font-bold text-slate-600 mt-3 text-center">{L.meditating?.book || '魔法書'}</div>
                </div>

                {/* 粒子流 2 — 書本 → 水晶 */}
                <div className="absolute top-1/2 left-[55%] right-[18%] h-2 -translate-y-1/2 pointer-events-none">
                  {[0, 0.3, 0.6, 0.9].map((d, i) => (
                    <span
                      key={i}
                      className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full shadow-md animate-particle-flow-2"
                      style={{
                        animationDelay: `${d}s`,
                        background: i % 2 === 0 ? COLORS.yellow : COLORS.main,
                      }}
                    />
                  ))}
                </div>

                {/* 右：策略水晶 */}
                <div className="relative flex flex-col items-center">
                  <div className="relative w-24 h-24 animate-crystal-pulse">
                    {/* 水晶背景光暈 */}
                    <div className="absolute -inset-4 bg-gradient-to-br from-amber-300 to-orange-500 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                    {/* 水晶主體（菱形）— 移除 animate-hue，維持品牌橘不變色 */}
                    <div className="relative w-24 h-24 bg-gradient-to-br from-amber-300 via-orange-400 to-orange-500 rotate-45 rounded-2xl shadow-2xl flex items-center justify-center">
                      <div className="absolute inset-0 rounded-2xl bg-white/30 animate-pulse"></div>
                      <Sparkles className="text-white -rotate-45 relative z-10" size={32} />
                    </div>
                  </div>
                  <div className="text-xs font-bold text-slate-600 mt-5">{L.meditating?.crystal || '策略水晶'}</div>
                  {/* 水晶周圍光點 */}
                  <Sparkles className="absolute -top-2 -right-2 text-amber-400 animate-float" size={16} />
                  <Sparkles className="absolute top-12 -left-3 animate-float" size={14} style={{ color: COLORS.main, animationDelay: '0.5s' }} />
                  <Sparkles className="absolute top-2 left-2 animate-float" size={10} style={{ color: COLORS.riskHigh, animationDelay: '1.2s' }} />
                </div>

              </div>

              <h2 className="text-4xl font-black mb-3 tracking-tight text-slate-900">{L.meditating?.building || '精靈正在為您打造策略...'}</h2>

              {/* 步驟顯示 — 大 pill 含背景 */}
              <div className="px-6 py-3 rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 font-black text-xl md:text-2xl shadow-md mb-5 max-w-lg" style={{ color: COLORS.main }}>
                {meditationText}
              </div>

              <div className="w-80 h-3 bg-slate-200 rounded-full overflow-hidden p-0.5 border border-white mb-4">
                <div className="h-full bg-gradient-to-r from-amber-300 via-orange-400 to-orange-500 bg-[length:200%_100%] animate-loading-bar rounded-full"></div>
              </div>

              {/* AI 思考路徑卡 — 模擬精靈正在跑 AI pipeline 的清單，每 1.5 秒推進一階 */}
              <div className="bg-white rounded-2xl p-5 border-2 border-orange-100 shadow-md w-full max-w-md">
                <p className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: COLORS.main }}>{L.meditating?.aiPath || '精靈回測路徑'}</p>
                {[
                  { icon: '/images/icon/genie/animation_1.png', label: L.meditating?.t1 || '為您回測候選策略', detail: `247 ${L.meditating?.sets || '組'}` },
                  { icon: '/images/icon/genie/animation_2.png', label: L.meditating?.t2 || '依您的風險偏好分析', detail: `89 ${L.meditating?.sets || '組'}` },
                  { icon: '/images/icon/genie/animation_3.png', label: L.meditating?.t3 || '計算雷達向量與 KPI 分數', detail: `24 ${L.meditating?.sets || '組'}` },
                  { icon: '/images/icon/genie/animation_4.png', label: L.meditating?.t4 || '為您匹配最適合的策略', detail: `3 ${L.meditating?.sets || '組'}` },
                ].map((step, i) => {
                  const done = i < traceIdx;
                  const active = i === traceIdx;
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-3 py-2 transition-opacity duration-500 ${
                        done || active ? 'opacity-100' : 'opacity-40'
                      }`}
                    >
                      {/* 用 mask 把黑色 icon 填成品牌橘 #F36D26（精準上色，不用 filter 近似）*/}
                      <span
                        className="shrink-0"
                        style={{
                          width: 24, height: 24, display: 'inline-block', backgroundColor: COLORS.main,
                          WebkitMaskImage: `url(${step.icon})`, maskImage: `url(${step.icon})`,
                          WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
                          WebkitMaskSize: 'contain', maskSize: 'contain',
                          WebkitMaskPosition: 'center', maskPosition: 'center',
                        }}
                      />
                      <span className={`flex-1 text-sm md:text-base font-bold ${done ? 'text-slate-800' : active ? '' : 'text-slate-500'}`} style={active ? { color: COLORS.main } : undefined}>
                        {step.label}
                        {(done || active) && step.detail && (
                          <span className="ml-2 text-xs text-slate-500 font-medium">{step.detail}</span>
                        )}
                      </span>
                      <span className="w-6 h-6 flex items-center justify-center shrink-0">
                        {done && <CheckCircle2 size={18} style={{ color: COLORS.main }} />}
                        {active && (
                          <span className="inline-block w-3 h-3 rounded-full animate-ping" style={{ background: COLORS.main }} />
                        )}
                        {!done && !active && (
                          <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-300" />
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            </>
          )}

          {/* 螢幕四：方案揭曉 Step 3: The Revelation
              依 ambition → riskTier 從 /api/mas-genie/elites 拉策略，
              frontend 排序切守/衡/銳三段、session seed 抽 1 + symbol 多樣化 */}
          {step === 'revelation' && (() => {
            const school = getSchool(selectedAmbition);
            // composedCards 由 useMemo cache，這裡只取用
            const hasCards = composedCards.length > 0;
            return (
            <div className="w-full animate-in fade-in zoom-in-95 duration-700">

              {/* Hero block — 包整個 Revelation 區（截圖一 spec） */}
              <div className="bg-white rounded-[32px] p-6 md:p-8 border border-slate-100 shadow-md relative">
                {/* 右上角「重新選擇」橘色純外框按鈕 */}
                <button
                  onClick={() => { setStep('ambition'); }}
                  className="absolute top-5 right-5 md:top-6 md:right-6 px-3 py-1.5 rounded-xl text-xs md:text-sm font-bold transition-colors"
                  style={{ border: '1.5px solid #f97316', color: COLORS.main, background: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#ea580c'; e.currentTarget.style.borderColor = '#ea580c'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = COLORS.main; e.currentTarget.style.borderColor = COLORS.main; }}
                >
                  {L.btn?.reselect || '重新選擇'}
                </button>

                <div className="text-center mb-5 pt-8 md:pt-2">
                  <h2 className="mb-2" style={{ ...TYPO.headingL, color: '#191C1D' }}>{L.revelation?.title || '已為您匹配最佳方案！'}</h2>
                  <p style={{ ...TYPO.bodyM, color: COLORS.grayDark }}>{L.revelation?.subtitle || '先看這 3 個策略，選擇您想要的回報，實際表現可能不同。'}</p>
                  {poolSize > 0 && composedCards.length < 3 && (
                    <p style={{ ...TYPO.bodyS, color: COLORS.main, marginTop: 8 }}>{fillTemplate(L.revelation?.poolSmall || '✨ 精靈已為您回測出 {n} 組符合您風險的策略', { pool: poolSize, n: composedCards.length })}</p>
                  )}
                  {wishMeta.usedFallback && (
                    <p className="max-w-lg mx-auto" style={{ ...TYPO.bodyS, color: COLORS.main, marginTop: 8 }}>
                      {L.revelation?.usedFallback || '✨ 符合您風險的精選策略有限，精靈已額外為您回測其他風險等級的策略，補足更完整的選擇'}
                    </p>
                  )}
                  {wishMeta.excludedRecent && (
                    <div className="mt-3 flex justify-center">
                      <span className="px-3 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200" style={{ ...TYPO.bodyS, fontWeight: 700 }}>{L.revelation?.excludedRecent || '🔄 已為您換上與上次不同的策略'}</span>
                    </div>
                  )}
                </div>

              {/* 池子全空 / 載入中 / 錯誤 — 顯示對應提示 */}
              {!hasCards && (
                <div className="max-w-xl mx-auto bg-white rounded-[32px] p-10 border-2 border-dashed border-slate-300 text-center">
                  <p className="text-2xl font-black text-slate-800 mb-3">
                    {revelationLoading ? (L.revelation?.preparing || '精靈正在為您回測策略...') : (L.revelation?.poolPreparing || '精靈策略回測準備中')}
                  </p>
                  <p className="text-base text-slate-600 mb-6">
                    {revelationLoading
                      ? (L.revelation?.loadingDesc || '連線中，精靈正為您回測最適合的精靈策略，請稍候片刻')
                      : revelationError
                        ? fillTemplate(L.revelation?.loadFail || '載入失敗：{msg}', { msg: revelationError })
                        : (L.revelation?.notReady || '目前此風險的策略還在為您回測中，請稍後再試或調整您的目標。')}
                  </p>
                  {!revelationLoading && (
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() => setStep('ambition')}
                        className="px-5 py-2.5 rounded-2xl border-2 border-slate-300 bg-white hover:border-slate-500 text-slate-800 font-bold transition-all"
                      >
                        <ArrowLeft size={16} className="inline mr-1" /> {L.btn?.backToRisk || '返回挑選風險'}
                      </button>
                      <button
                        onClick={() => setStep('meditating')}
                        className="px-5 py-2.5 rounded-2xl text-white font-bold hover:brightness-110 transition-all"
                        style={{ background: COLORS.main }}
                      >
                        {L.btn?.retry || '重新嘗試'}
                      </button>
                    </div>
                  )}
                </div>
              )}

                {/* 3 張卡（外框 1112 = 3 卡 + gap24×2；卡片 justify-self stretch 等比縮放）*/}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 items-stretch" style={{ gap: 40 }}>
                  {composedCards.map((card) => <RevelationCard key={card.id} card={card} />)}
                </div>
              </div>
            </div>
          )})()}

          </div>{/* 步驟內容置中容器 end */}
        </div>
      </MDBox>
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 15s linear infinite;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(-5%); }
          50% { transform: translateY(5%); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 4s ease-in-out infinite;
        }
        @keyframes loading-bar {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-loading-bar {
          animation: loading-bar 3s linear infinite;
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.5; }
          25% { transform: translate(8px, -10px) rotate(15deg); opacity: 1; }
          50% { transform: translate(-4px, -16px) rotate(-10deg); opacity: 0.7; }
          75% { transform: translate(10px, -6px) rotate(8deg); opacity: 1; }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        @keyframes hue-shift {
          0% { filter: hue-rotate(0deg) saturate(1); }
          50% { filter: hue-rotate(40deg) saturate(1.15); }
          100% { filter: hue-rotate(0deg) saturate(1); }
        }
        .animate-hue {
          animation: hue-shift 6s ease-in-out infinite;
        }

        /* === 翻書動畫專用 === */
        @keyframes book-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-book-float {
          animation: book-float 3.5s ease-in-out infinite;
        }
        @keyframes page-flip {
          0%, 60%, 100% { transform: rotateY(0deg); opacity: 1; }
          30% { transform: rotateY(-160deg); opacity: 0.8; }
        }
        .animate-page-flip {
          animation: page-flip 2.4s ease-in-out infinite;
          backface-visibility: hidden;
        }
        @keyframes stream-up {
          0% { transform: translateY(0) scale(0.6); opacity: 0; }
          20% { opacity: 1; }
          100% { transform: translateY(-50px) scale(1.2); opacity: 0; }
        }
        .animate-stream-up {
          animation: stream-up 1.8s ease-out infinite;
        }
        @keyframes particle-flow-1 {
          0% { left: 0%; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        .animate-particle-flow-1 {
          animation: particle-flow-1 1.6s ease-in-out infinite;
        }
        @keyframes particle-flow-2 {
          0% { left: 0%; opacity: 0; transform: translateY(-50%) scale(0.7); }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { left: 100%; opacity: 0; transform: translateY(-50%) scale(1.3); }
        }
        .animate-particle-flow-2 {
          animation: particle-flow-2 1.5s ease-in-out infinite;
        }
        @keyframes crystal-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.07); }
        }
        .animate-crystal-pulse {
          animation: crystal-pulse 2.4s ease-in-out infinite;
        }
      `}} />
      {/* ───────────────────────── ReportModal — 點 Revelation 卡的「查看計畫詳情」會跳出
          PO spec：取代原本 setStep('report') 跳整頁，改成 modal */}
      {selectedStrategy && (() => {
        const elite = selectedStrategy.elite || {};
        const bColor = selectedStrategy.bannerColor || COLORS.riskMid;
        const bLabel = selectedStrategy.bannerLabel || L.card?.bannerHeng || '平衡';
        const sTitle = eliteAiName(elite) || (selectedStrategy.strategyTypeKey && L.stype?.[`${selectedStrategy.strategyTypeKey}_name`]) || selectedStrategy.title || L.modal?.fallbackTitle || '策略詳情';
        const health = { label: L.health?.good || '優' };
        // 許願階段健康一律「優」；mas-genie 的 ⓘ tooltip 在 L.modal.infoX → shim 成元件預期的 L.info.X
        const modalL = {
          ...L,
          info: {
            symbol: L.modal?.infoSymbol, timeframe: L.modal?.infoTimeframe, tradeCount: L.modal?.infoTrade,
            health: L.modal?.infoHealth, mdd: L.modal?.infoMdd, sharpe: L.modal?.infoSharpe,
            sortino: L.modal?.infoSortino, pf: L.modal?.infoPf,
          },
        };
        return (
          <StrategyDetailModal
            open={reportModalOpen}
            onClose={() => setReportModalOpen(false)}
            L={modalL}
            title={sTitle}
            typeLabel={bLabel}
            typeColor={bColor}
            elite={elite}
            radarValues={selectedStrategy.radar}
            radarColor={bColor}
            radarKey={`rev-detail-${selectedStrategy.eliteId || selectedStrategy.id || ''}`}
            radarAxes={radarAxes}
            healthDisplay={`${health.label} ↑`}
            healthColor={COLORS.main}
            tfLabel={tfLabelL}
            footer={(
              <>
                <button
                  onClick={() => setReportModalOpen(false)}
                  style={{
                    display: 'flex', height: 48, padding: '12px clamp(10px, 4vw, 32px)', boxSizing: 'border-box',
                    flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap',
                    justifyContent: 'center', alignItems: 'center', gap: 10,
                    borderRadius: 8, border: 'none', background: COLORS.grayLightest,
                    color: COLORS.black, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif', ...TYPO.title, cursor: 'pointer',
                  }}
                >
                  {L.btn?.backToList || '返回方案列表'}
                </button>
                <button
                  onClick={async () => {
                    await handleSubscribe();
                    setReportModalOpen(false);
                  }}
                  disabled={subscribing}
                  style={{
                    display: 'flex', height: 48, padding: '12px clamp(10px, 4vw, 32px)', boxSizing: 'border-box',
                    flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap',
                    justifyContent: 'center', alignItems: 'center', gap: 10,
                    borderRadius: 8, border: 'none', background: COLORS.main,
                    color: COLORS.white, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif', ...TYPO.title,
                    cursor: subscribing ? 'not-allowed' : 'pointer', opacity: subscribing ? 0.6 : 1,
                  }}
                >
                  {subscribing ? (L.modal?.saving || '儲存中...') : (L.modal?.save || '儲存此策略')}
                </button>
              </>
            )}
          />
        );
      })()}

      {/* 使用策略精靈 confirm dialog（截圖二 spec：標題列 / 內文 / 剩餘配額+按鈕，皆以分隔線分區）*/}
      <Dialog
        open={wishConfirmOpen}
        onClose={() => setWishConfirmOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', width: '100%', maxWidth: 680 } }}
      >
        {(() => {
          const mLimit = userQuota?.monthlyLimit;
          const mRemain = userQuota?.monthlyRemaining ?? mLimit ?? 0;
          const unlimited = mLimit === -1;
          return (
            <>
              {/* 標題列 */}
              <div style={{ display: 'flex', height: 42, justifyContent: 'space-between', alignItems: 'center', padding: '0 24px', margin: '16px 0' }}>
                <span style={{ color: COLORS.black, ...TYPO.modalTitle }}>
                  {L.wishConfirm?.title || '使用策略精靈'}
                </span>
                <IconButton onClick={() => setWishConfirmOpen(false)} size="small" sx={{ color: COLORS.grayDark }}>
                  <X size={20} />
                </IconButton>
              </div>
              <div style={{ borderTop: '1px solid #EFEFEF' }} />
              {/* 內文 */}
              <div style={{ display: 'flex', padding: 24, flexDirection: 'column', alignItems: 'flex-start', gap: 16 }}>
                <p style={{ color: COLORS.black, ...TYPO.title, margin: 0 }}>
                  {L.wishConfirm?.bodyA || '即將消耗 '}<span style={{ color: COLORS.main }}>{L.wishConfirm?.bodyHighlight || '1 次本月配額'}</span>{L.wishConfirm?.bodyB || '，精靈會依您選的目標為您挑出 3 個策略。'}
                </p>
              </div>
              <div style={{ borderTop: '1px solid #EFEFEF' }} />
              {/* 剩餘配額 + 按鈕 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px' }}>
                <span style={{ flex: '1 1 0', minWidth: 0, color: COLORS.grayDark, ...TYPO.title, fontWeight: 400 }}>
                  {unlimited
                    ? (L.wishConfirm?.unlimited || '本方案無月配額限制')
                    : <>{fillTemplate(L.wishConfirm?.remain || '剩餘配額：{remain} 次 ', { remain: mRemain })}<span style={{ color: COLORS.main }}>{fillTemplate(L.wishConfirm?.remainAfter || '(此次扣完剩 {after} 次)', { after: Math.max(0, mRemain - 1) })}</span></>}
                </span>
                <button
                  onClick={() => setWishConfirmOpen(false)}
                  style={{
                    display: 'flex', minWidth: 96, padding: '12px 32px', boxSizing: 'border-box',
                    justifyContent: 'center', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap',
                    borderRadius: 8, border: 'none', background: COLORS.grayLightest,
                    color: COLORS.black, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
                    ...TYPO.title, cursor: 'pointer',
                  }}
                >
                  {L.btn?.cancel || '取消'}
                </button>
                <button
                  onClick={() => { setWishConfirmOpen(false); setStep('meditating'); }}
                  style={{
                    display: 'flex', minWidth: 128, padding: '12px 32px', boxSizing: 'border-box',
                    justifyContent: 'center', alignItems: 'center', flexShrink: 0,
                    borderRadius: 8, border: 'none', background: COLORS.main,
                    color: COLORS.white, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
                    ...TYPO.title, cursor: 'pointer',
                  }}
                >
                  {L.btn?.confirm || '確認'}
                </button>
              </div>
            </>
          );
        })()}
      </Dialog>

      {/* 配額警告 Dialog（截圖三 spec：標題列 / 內文 / 按鈕，分隔線分區；monthly-full 標題紅字 + 三段式內文）*/}
      <Dialog
        open={!!quotaWarning}
        onClose={() => setQuotaWarning(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {quotaWarning && (() => {
          const danger = quotaWarning.type === 'monthly-full';
          return (
            <>
              {/* 標題列 */}
              <div style={{ display: 'flex', height: 42, justifyContent: 'space-between', alignItems: 'center', padding: '0 24px', margin: '16px 0' }}>
                <span style={{ color: danger ? COLORS.error : COLORS.black, ...TYPO.modalTitle }}>
                  {quotaWarning.title}
                </span>
                <IconButton onClick={() => setQuotaWarning(null)} size="small" sx={{ color: COLORS.grayDark }}>
                  <X size={20} />
                </IconButton>
              </div>
              <div style={{ borderTop: '1px solid #EFEFEF' }} />
              {/* 內文 — 有結構化欄位時三段式（主黑/警示紅/提示灰），否則純文字 */}
              <div style={{ display: 'flex', padding: 24, flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
                {quotaWarning.bodyMain ? (
                  <>
                    <p style={{ color: COLORS.black, ...TYPO.title, margin: 0 }}>
                      {quotaWarning.bodyMain}
                    </p>
                    {quotaWarning.bodyAlert && (
                      <p style={{ color: COLORS.error, ...TYPO.bodyM, margin: 0 }}>
                        {quotaWarning.bodyAlert}
                      </p>
                    )}
                    {quotaWarning.bodyHint && (
                      <p style={{ color: COLORS.grayDark, ...TYPO.bodyM, margin: 0 }}>
                        {quotaWarning.bodyHint}
                      </p>
                    )}
                  </>
                ) : (
                  <p style={{ whiteSpace: 'pre-line', color: COLORS.black, ...TYPO.title, margin: 0 }}>
                    {quotaWarning.body}
                  </p>
                )}
              </div>
              <div style={{ borderTop: '1px solid #EFEFEF' }} />
              {/* 按鈕（右對齊；次要=灰，主要=橘，樣式同取消/確認）*/}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: '16px 24px' }}>
                {quotaWarning.secondaryAction && (
                  <button
                    onClick={quotaWarning.secondaryAction.onClick}
                    style={{
                      display: 'flex', padding: '12px 32px', boxSizing: 'border-box',
                      justifyContent: 'center', alignItems: 'center', flexShrink: 0,
                      borderRadius: 8, border: 'none', background: COLORS.grayLightest,
                      color: COLORS.black, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
                      ...TYPO.title, cursor: 'pointer',
                    }}
                  >
                    {quotaWarning.secondaryAction.label}
                  </button>
                )}
                {quotaWarning.primaryAction && (
                  <button
                    onClick={quotaWarning.primaryAction.onClick}
                    style={{
                      display: 'flex', minWidth: 128, padding: '12px 32px', boxSizing: 'border-box',
                      justifyContent: 'center', alignItems: 'center', flexShrink: 0,
                      borderRadius: 8, border: 'none', background: COLORS.main,
                      color: COLORS.white, textAlign: 'center', fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif',
                      ...TYPO.title, cursor: 'pointer',
                    }}
                  >
                    {quotaWarning.primaryAction.label}
                  </button>
                )}
              </div>
            </>
          );
        })()}
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default MASGenie;
